<template>
  <div class="property-behavior">
    <ontos-pair-toggle v-if="offersMultiple" :model-value="!!model.multiple" v-bind="pairs.multiple"
                       @update:model-value="$emit('multiple', $event)">
      <!-- O mínimo e o máximo são regra de quem PREENCHE: numa lista calculada de sempre não há quem, nem no
           espelho, cuja lista é feita pelos itens do outro lado. No "Calculado quando", valem enquanto ela é
           preenchida (`fillable`) -->
      <!-- Um interruptor para cada limite (pedido do usuário): desligado, o que vale no lugar do campo — "0" e
           "Ilimitado" —; ligado, o campo de número do Vuetify, com o − e o +, já com um número. Era uma caixa
           "Ilimitado" com o máximo apagado mostrando um valor velho, e depois o máximo vazio querendo dizer
           "sem limite" — os dois estranhos -->
      <div v-if="fillable && kind !== 'mirror'" class="property-behavior-count">
        <div v-for="limit of limits" :key="limit.key" class="property-behavior-limit">
          <v-switch :model-value="limited[limit.key]" :label="$tt(limit.label)" color="primary" density="compact"
                    hide-details @update:model-value="setLimited(limit.key, !!$event)"/>
          <v-number-input v-if="limited[limit.key]" :model-value="numberOf(model[limit.field])"
                          @update:model-value="setLimit(limit.key, $event)"
                          @update:focused="!$event && $nextTick(() => settle(limit.key))" :aria-label="$tt(limit.label)"
                          :min="floorOf(limit.key)" :max="ceilingOf(limit.key)" :precision="0" control-variant="split"
                          density="compact" hide-details
                          class="property-behavior-number"/>
          <span v-else class="text-body-1 text-medium-emphasis">{{ limit.key === 'min' ? 0 : $tt('unlimited') }}</span>
        </div>
      </div>
    </ontos-pair-toggle>

    <!-- Também na lista, onde Obrigatório é ter mínimo (pedido do usuário) — ver o `setRequired`. O "quando"
         abre embaixo a fórmula que decide, item a item -->
    <ontos-choice-toggle v-if="kind !== 'mirror' && fillable" :model-value="requiredMode"
                         :label="pairs.required.label" :options="requiredOptions"
                         @update:model-value="setRequired($event)">
      <template v-if="requiredMode === 'when' && bindings" #default>
        <ontos-formula-input ref="requiredWhen" class="required" v-bind="bindings" :label="$tt('pair.required-when')"
                             :value="model.requiredWhen || ''" condition="requiredWhen" :draft="conditionDraft"
                             :result-label="booleanLabel" :example="requiredWhenExample"
                             @input="model.requiredWhen = $event"/>
      </template>
    </ontos-choice-toggle>
    <ontos-pair-toggle v-if="offersUnique" :model-value="!!model.unique" v-bind="pairs.unique"
                       @update:model-value="model.unique = $event"/>
    <ontos-pair-toggle v-if="filledSingle && offersIdentifier" :model-value="!!model.identifier" v-bind="pairs.identifier"
                       @update:model-value="setIdentifier($event)"/>

    <!-- O Preenchimento do relacionamento, na caixa do dono. O do atributo mora embaixo do Tipo (ver o
         `PropertyFilling`) -->
    <ontos-property-filling v-if="kind === 'relationship'" :model="model" kind="relationship"
                            :compute-blocked="computeBlocked"/>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import IdeaProperty, {fillable} from '@/types/IdeaProperty'
import PairToggle from '@/components/shared/PairToggle.vue'
import ChoiceToggle, {ChoiceOption} from '@/components/shared/ChoiceToggle.vue'
import AsyncFormulaInput from '@/components/design/property/asyncFormulaInput'
import PropertyFilling from '@/components/design/property/PropertyFilling.vue'
import {editorsValid} from '@/components/design/property/editors'
import {Pair, pairOf} from '@/components/design/property/pairs'
import {isMedia} from '@/utils/mediaTypes'
import {identifierBlocker, MARKS} from '@/utils/propertyDefinition'
import types from '@/model/types'

/** As três respostas da Obrigatoriedade: nunca, sempre, ou quando a fórmula dá Sim. */
type RequiredMode = 'optional' | 'always' | 'when'

/** O que a seção mexe no modelo do editor — os do atributo e do relacionamento têm estes campos. */
export type BehaviorModel = {
  type?: string, multiple: boolean, required: boolean, unique: boolean, identifier: boolean, computed: boolean,
  /** O "Obrigatório quando": a fórmula, e o texto vazio enquanto ela ainda não foi escrita. Ausente é não ter. */
  requiredWhen?: string,
  /** A condição do "Calculado quando", do mesmo jeito; a fórmula do cálculo é o `formula` do editor. */
  computedWhen?: string,
  /** O máximo vazio é sem limite. */
  minItems: number | string, maxItems: number | string | null,
  /** No relacionamento: o calculado dele não compõe o nome, e o do atributo compõe (`identifierBlocker`). */
  relationship?: boolean
}

/**
 * O mínimo e o máximo de itens no modelo, a partir da propriedade gravada. Os dois editores faziam cada
 * um o seu, e o do atributo lia o "sem limite" de um campo que não existe (`itemCount.maxItems`).
 */
export function behaviorModelOf(value: IdeaProperty): Pick<BehaviorModel, 'minItems' | 'maxItems' | 'requiredWhen' | 'computedWhen'> {
  const count = value.itemCount
  //Os "quando" declarados mesmo sem valor: o modelo é observado inteiro, e a chave que falta não seria vista
  const conditions = {requiredWhen: value.requiredWhen, computedWhen: value.computedWhen}
  return count ? {minItems: count.min, maxItems: count.max ?? null, ...conditions} : {minItems: 0, maxItems: null, ...conditions}
}

const strong = (value: any) => value || undefined
const positive = (value: any): number | undefined => Number(value) > 0 ? Number(value) : undefined

/**
 * O que a seção decide, como vai gravado — o par do `behaviorModelOf`, igual no atributo e no
 * relacionamento:
 * - obrigatório no que alguém preenche: no valor único, a escolha; na lista, ter mínimo (pedido do
 *   usuário: mínimo 0 é Opcional, 1 ou mais é Obrigatório);
 * - o "Obrigatório quando" no lugar do obrigatório de sempre — os dois não se somam —, e na lista só com
 *   mínimo, que é o que ele liga;
 * - exclusivo só no valor único;
 * - o mínimo e o máximo só na lista que alguém preenche, e nada quando não dizem nada;
 * - o identificador só quando pode (`identifierBlocker`).
 *
 * "Que alguém preenche" inclui o "Calculado quando" (`fillable`): tudo isso vale enquanto ele é preenchido.
 */
export function behaviorValue(model: BehaviorModel) {
  const byPeople = fillable(model)
  const min = positive(model.minItems)
  const max = positive(model.maxItems)
  const requiredWhen = !byPeople || (model.multiple && !min) ? undefined : strong(model.requiredWhen)
  const required = !byPeople || requiredWhen ? undefined : model.multiple ? (min ? true : undefined) : strong(model.required)
  return {
    multiple: strong(model.multiple),
    required,
    requiredWhen,
    //Só no atributo calculado: o relacionamento gravado com sobreposição quebraria o reverso e o espelho
    computedWhen: model.computed && !model.relationship ? strong(model.computedWhen) : undefined,
    unique: byPeople && !model.multiple ? strong(model.unique) : undefined,
    //Um dos dois pode faltar — sem mínimo, ou sem limite —, e o servidor aceita; o tipo é que exige os dois
    itemCount: model.multiple && byPeople && (min || max) ? {min, max} as IdeaProperty['itemCount'] : undefined,
    identifier: !identifierBlocker(model) && model.identifier ? true : undefined
  }
}

/**
 * A seção Comportamento da janela de propriedade: vários valores, obrigatório, exclusivo e compõe o nome,
 * cada um num par de dois lados com a frase do escolhido (pedido do usuário: no lugar das
 * caixas de marcar). Estava escrita igual — com o mínimo e o máximo — no editor de atributo e no de
 * relacionamento.
 *
 * Mexe direto no `model` do editor (os campos dele, e não o objeto): é o editor que observa o modelo e
 * entrega a propriedade. O "Vários valores" sai como evento (`multiple`): o atributo de seleção descarta
 * o padrão ao trocá-lo, e só ele sabe.
 *
 * O lado ligado de cada par tem o ícone e a cor da marca (`pairOf`), os mesmos dos selos da lista. O
 * Preenchimento é à parte (`PropertyFilling`): no atributo mora embaixo do Tipo; no relacionamento, aqui.
 */
@Component({
  name: 'ontos-property-behavior', emits: ['multiple'],
  components: {
    'ontos-pair-toggle': PairToggle, 'ontos-choice-toggle': ChoiceToggle, 'ontos-formula-input': AsyncFormulaInput,
    'ontos-property-filling': PropertyFilling
  }
})
class PropertyBehavior extends Vue {
  @Prop({type: Object, required: true}) readonly model!: BehaviorModel
  /**
   * `relationship`: o dono — a Cardinalidade e o resto, sem a Unicidade, que é a Cardinalidade da caixa
   * de lá (pedido do usuário). `mirror`: o lado de lá — só a Cardinalidade, derivada do dono; o `model`
   * vem do editor já na visão do espelho (ver o `RelationshipInput.behaviorOf`).
   */
  @Prop({type: String, required: true}) readonly kind!: 'attribute' | 'relationship' | 'mirror'
  /** Por que o "Calculado" não se liga — o relacionamento com espelho —, ou vazio. */
  @Prop({type: String, default: ''}) readonly computeBlocked!: string
  /**
   * A frase da Cardinalidade no lugar da de sempre ("Um valor só por item"): no relacionamento, o sentido
   * da associação que sai deste lado — "Cada Canção tem até 1 Gênero musical" (pedido do usuário).
   */
  @Prop({type: String, default: ''}) readonly cardinalityHint!: string
  /**
   * O que o editor de fórmula recebe igual em todo formulário (`PropertyInput.formulaBindings`), para a
   * fórmula do "Obrigatório quando". Sem ele — no espelho, que não tem obrigatoriedade —, não há fórmula.
   */
  @Prop({type: Object, default: undefined}) readonly bindings?: Record<string, unknown>
  /** A propriedade como está no formulário, que a checagem da fórmula usa. */
  @Prop({type: Object, default: () => ({})}) readonly draft!: Partial<IdeaProperty>
  /** O que se escreve no "Obrigatório quando", com os nomes da coleção, enquanto ele está vazio (`formulaExamples`). */
  @Prop({type: String, default: ''}) readonly requiredWhenExample!: string

  //Exposto ao template: a condição dá Sim/Não, o nome que o app dá ao tipo
  booleanLabel = types.boolean.name

  /**
   * A fórmula escrita antes de trocar de opção: voltar ao "quando" a traz de volta, em vez de recomeçar
   * do vazio. Só nesta janela; o que se grava é o que estiver escolhido ao salvar.
   */
  lastRequiredWhen = ''

  /**
   * O rascunho que a checagem do "quando" vê: sem o obrigatório de sempre, que é o que a escolha diz. O
   * servidor põe o rascunho sobre a propriedade gravada, e o `required` dela recusaria a condição.
   */
  get conditionDraft(): Partial<IdeaProperty> {
    return {...this.draft, required: false}
  }

  /** Mídia é um arquivo por item, e o sim/não já é um valor só: nenhum dos dois vira lista. */
  get offersMultiple(): boolean {
    return this.kind !== 'attribute' || (this.model.type !== 'boolean' && !isMedia(this.model.type))
  }

  /** Um valor só, que alguém preenche — também o "Calculado quando": onde valem o obrigatório e compor o nome. */
  get filledSingle(): boolean {
    return this.kind !== 'mirror' && !this.model.multiple && fillable(this.model)
  }

  /** Alguém preenche o campo, sempre ou às vezes (`fillable`): o mínimo, o máximo e a obrigatoriedade valem. */
  get fillable(): boolean {
    return fillable(this.model)
  }

  /**
   * Só no atributo. No relacionamento, o exclusivo é a Cardinalidade do outro lado — "cada Cliente em um
   * Pedido só" é o "Um valor" da caixa do Cliente —, e mora lá (pedido do usuário).
   */
  get offersUnique(): boolean {
    return this.kind === 'attribute' && this.filledSingle && this.model.type !== 'boolean' && !isMedia(this.model.type)
  }

  /** Os dois limites da lista: o interruptor de cada um, e o campo que ele mostra. */
  limits = [
    {key: 'min', field: 'minItems', label: 'min-items'},
    {key: 'max', field: 'maxItems', label: 'max-items'}
  ] as const

  /**
   * Até onde o campo desce. O piso de cada um é o número que DESFAZ o limite (`setLimit`) — o mínimo em 0 é
   * não ter mínimo, e o máximo em 1 é um valor só —, e o máximo não desce abaixo do mínimo (pedido do
   * usuário).
   */
  floorOf(key: 'min' | 'max'): number {
    return key === 'min' ? 0 : Math.max(1, (this.limited.min && positive(this.model.minItems)) || 0)
  }

  /** Até onde o campo sobe: o mínimo não passa do máximo, quando há máximo (pedido do usuário). */
  ceilingOf(key: 'min' | 'max'): number | undefined {
    return key === 'min' && this.limited.max ? positive(this.model.maxItems) : undefined
  }

  /** O número do campo, que o modelo guarda também como texto (o que veio de um campo de texto). */
  numberOf(value: number | string | null): number | null {
    return value === null || value === '' ? null : Number(value)
  }

  /**
   * O número digitado ou dado pelo − e o +. No piso, o limite se desfaz (pedido do usuário): o mínimo em 0
   * desliga o interruptor, que volta a dizer "0" — é o mesmo que não ter mínimo —, e o máximo em 1 vira
   * "Um valor", que é o que uma lista de no máximo 1 é. Vazio não: é o campo apagado para digitar outro, e
   * desfazer ali atrapalharia a digitação.
   */
  setLimit(key: 'min' | 'max', value: number | null) {
    const previous = this.numberOf(key === 'min' ? this.model.minItems : this.model.maxItems)
    if (key === 'min') this.model.minItems = value === null ? '' : value
    else this.model.maxItems = value
    //Pelo −, um passo abaixo do que estava: o piso desfaz já. Digitado, só ao sair do campo (`settle`) —
    //"12" passa pelo "1", e o máximo viraria um valor só no meio da digitação
    if (value !== null && previous !== null && value === previous - 1) this.settle(key)
  }

  /**
   * O piso desfaz o limite: o mínimo em 0 desliga o interruptor, e o máximo em 1 vira "Um valor". Ao sair do
   * campo, com o número já dentro dos limites — o campo do Vuetify só aceita, digitando, o que cabe entre o
   * `min` e o `max`, e põe no limite o resto ao sair (o máximo nunca abaixo do mínimo).
   */
  settle(key: 'min' | 'max') {
    const value = this.numberOf(key === 'min' ? this.model.minItems : this.model.maxItems)
    if (key === 'min' && value === 0) this.setLimited('min', false)
    if (key === 'max' && value === 1) {
      this.setLimited('max', false)
      this.$emit('multiple', false)
    }
  }

  /**
   * A Obrigatoriedade escolhida. Com fórmula (ou começando a escrevê-la), "quando"; senão, na lista,
   * Obrigatório é o interruptor do mínimo; no valor único, o que está gravado.
   */
  get requiredMode(): RequiredMode {
    if (this.model.requiredWhen !== undefined) return 'when'
    return (this.model.multiple ? this.limited.min : this.model.required) ? 'always' : 'optional'
  }

  /** As três respostas, com a frase de cada uma; as duas obrigatórias, com a marca do obrigatório. */
  get requiredOptions(): ChoiceOption<RequiredMode>[] {
    const {off, on} = this.pairs.required
    return [
      {value: 'optional', ...off},
      {value: 'always', ...on},
      {value: 'when', label: on.label, qualifier: this.$tt('pair.when') as string,
        hint: this.$tt('pair.required-when-hint') as string, ...MARKS.required, conditional: true}
    ]
  }

  /**
   * Virou lista com Obrigatório (sempre ou quando): o mínimo liga em 1 — na lista, obrigatório é ter
   * mínimo, e sem ele a escolha passaria a dizer Opcional sozinha.
   */
  @Watch('model.multiple')
  onMultipleChanged(multiple: boolean) {
    const required = this.model.required === true || this.model.requiredWhen !== undefined
    if (multiple && required && !this.limited.min) this.setLimited('min', true)
  }

  /**
   * Os interruptores ligados. À parte do número, e não "tem número": apagar o campo para digitar outro
   * desligaria o interruptor no meio da digitação. Abrem como está gravado — com o limite, ligados.
   */
  limited: Record<'min' | 'max', boolean> = {min: false, max: false}

  created() {
    this.limited = {min: positive(this.model.minItems) !== undefined, max: positive(this.model.maxItems) !== undefined}
  }

  /**
   * Liga com um número já no campo — o mínimo em 1, o máximo em 10, ou no mínimo se ele for maior —, e
   * desliga apagando o limite, que é o que "sem mínimo" e "sem máximo" sempre foram (`behaviorModelOf`).
   */
  setLimited(key: 'min' | 'max', on: boolean) {
    this.limited[key] = on
    if (key === 'max') {
      this.model.maxItems = on ? Math.max(10, positive(this.model.minItems) || 0) : null
      return
    }
    this.model.minItems = on ? 1 : 0
    //Na lista, o mínimo é o Obrigatório (pedido do usuário). Sem mínimo, o "quando" não tem o que ligar, e
    //vira Opcional; com ele, o "quando" continua — e não vira o de sempre
    if (!this.model.multiple) return
    if (!on) this.forgetRequiredWhen()
    this.model.required = on && this.model.requiredWhen === undefined
  }

  /** Sai do "quando", guardando a fórmula para a volta (`lastRequiredWhen`). */
  forgetRequiredWhen() {
    if (this.model.requiredWhen !== undefined) this.lastRequiredWhen = this.model.requiredWhen
    this.model.requiredWhen = undefined
  }

  /**
   * O que pode compor o nome dos itens (`identifierBlocker`). O obrigatório que falta — nunca ou só às
   * vezes — não impede: compor o nome liga o de sempre junto, que o identificador exige — e a frase do
   * par diz.
   */
  get offersIdentifier(): boolean {
    const blocker = identifierBlocker(this.model)
    return !blocker || blocker === 'not-required' || blocker === 'required-condition'
  }

  setIdentifier(identifier: boolean) {
    this.model.identifier = identifier
    if (!identifier) return
    this.forgetRequiredWhen()
    this.model.required = true
  }

  /**
   * Opcional ou "quando" não compõem o nome: deixa de compô-lo junto. O "quando" volta com a fórmula de
   * antes, quando houve (`lastRequiredWhen`).
   */
  setRequired(mode: RequiredMode) {
    if (mode === 'when') this.model.requiredWhen = this.model.requiredWhen ?? this.lastRequiredWhen
    else this.forgetRequiredWhen()
    this.model.required = mode === 'always'
    if (mode !== 'always') this.model.identifier = false
    //Na lista, obrigatório é ter mínimo: a escolha liga e desliga o interruptor dele (pedido do usuário)
    const minimum = mode !== 'optional'
    if (this.model.multiple && this.limited.min !== minimum) this.setLimited('min', minimum)
  }

  /** Antes de salvar: a fórmula do "quando", quando há, confere — ver o `PropertyInput.validEditors`. */
  validate(): boolean {
    return editorsValid(this.$refs.requiredWhen)
  }

  get pairs(): Record<'multiple' | 'required' | 'unique' | 'identifier', Pair> {
    const pair = pairOf.bind(null, this.$tt)
    const multiple = pair('multiple', 'pair.cardinality', ['pair.single', 'pair.single-hint'], ['pair.multiple', 'pair.multiple-hint'])
    //No relacionamento, a frase da Cardinalidade é o sentido da associação (pedido do usuário)
    if (this.cardinalityHint) {
      multiple.off.hint = this.cardinalityHint
      multiple.on.hint = this.cardinalityHint
    }
    return {
      multiple,
      required: pair('required', 'pair.requiredness', ['pair.optional', 'pair.optional-hint'], ['required', 'pair.required-hint']),
      unique: pair('unique', 'pair.uniqueness', ['pair.repeatable', 'pair.repeatable-hint'], ['unique', 'pair.unique-hint']),
      identifier: pair('identifier', 'pair.identification',
          ['pair.outside-name', 'pair.outside-name-hint'], ['pair.in-name', 'pair.in-name-hint'])
    }
  }
}

export default toNative(PropertyBehavior)
</script>

<style scoped>
.property-behavior {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Os dois limites um embaixo do outro (pedido do usuário) */
.property-behavior-count {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

/* O interruptor e, ligado, o número logo depois dele */
.property-behavior-limit {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* O número entre o − e o + */
.property-behavior-number {
  flex: 0 0 150px;
}
</style>
